'use client';

import type {Author} from 'iq-interview_guides/types';
import {useRef, useState} from 'react';
import {createPortal} from 'react-dom';

type ReviewerProps = {
  reviewer: Author;
  className?: string;
  nameClassName?: string;
};

export function Reviewer({reviewer, className = 'text-gray-500', nameClassName = 'text-gray-700'}: ReviewerProps) {
  const hasPopup = Boolean(reviewer.title || reviewer.bio);
  const ref = useRef<HTMLDivElement>(null);
  const [popupStyle, setPopupStyle] = useState<React.CSSProperties | null>(null);

  const showPopup = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    // Clamp inside the viewport: on narrow screens the popup would otherwise run off the right edge.
    const width = Math.min(320, window.innerWidth - 16);
    const left =
      window.innerWidth < 460
        ? (window.innerWidth - width) / 2
        : Math.min(Math.max(rect.left, 8), window.innerWidth - width - 8);
    setPopupStyle({top: rect.bottom + 8, left, width});
  };

  const hidePopup = () => setPopupStyle(null);

  const initials = reviewer.name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase())
    .slice(0, 2)
    .join('');

  return (
    <div
      ref={ref}
      className={`relative flex items-center gap-2 text-sm ${className} ${hasPopup ? 'cursor-pointer' : ''}`}
      onMouseEnter={hasPopup ? showPopup : undefined}
      onMouseLeave={hasPopup ? hidePopup : undefined}>
      <div className='w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden shrink-0 text-xs font-medium text-gray-600'>
        {reviewer.img_url ? (
          <img src={reviewer.img_url} alt={reviewer.name} className='w-full h-full object-cover' />
        ) : (
          <span>{initials}</span>
        )}
      </div>
      Reviewed by <strong className={nameClassName}>{reviewer.name}</strong>
      {hasPopup &&
        popupStyle &&
        createPortal(
          <div
            className='fixed z-9999 p-4 bg-white rounded-lg border border-gray-200 shadow-lg'
            style={popupStyle}>
            <strong>
              {reviewer.name}
              {reviewer.title && ` — ${reviewer.title}`}
            </strong>
            {reviewer.bio && <p className='mt-2 text-sm text-gray-600'>{reviewer.bio}</p>}
          </div>,
          document.body,
        )}
    </div>
  );
}
